// src/utils/legheFantacalcioExport.js
// Export rose in formato Leghe Fantacalcio: `$,$,$` prima di ogni squadra, poi righe
// `NomeSquadra,PlayerID,Crediti` dove PlayerID è l'ID interno di Fantacalcio.it
// (FANTACALCIO_LISTONE, vedi src/data/fantacalcioListone.js), diverso dal nostro
// playerId FantaMaster: va tradotto per nome+squadra al momento dell'export.
//
// Quando la corrispondenza nome+squadra non è univoca (o assente) si chiede
// conferma all'utente (vedi components/auction/AuctionTeamsTab.js, LegheResolutionOverlay);
// la scelta viene salvata in localStorage così non viene richiesta di nuovo ai prossimi export.

import { FANTACALCIO_LISTONE } from '../data/fantacalcioListone';

const OVERRIDES_KEY = 'fantapp_leghe_overrides_v1';

export function normalizeForMatch(value = '') {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

export function buildPlayerKey(name, team) {
  return `${normalizeForMatch(name)}|${normalizeForMatch(team)}`;
}

const LISTONE_INDEXED = FANTACALCIO_LISTONE.map((entry) => ({
  ...entry,
  normName: normalizeForMatch(entry.name),
  normTeam: normalizeForMatch(entry.team),
}));

const LISTONE_BY_TEAM = LISTONE_INDEXED.reduce((map, entry) => {
  if (!map.has(entry.normTeam)) map.set(entry.normTeam, []);
  map.get(entry.normTeam).push(entry);
  return map;
}, new Map());

const LISTONE_BY_ID = new Map(LISTONE_INDEXED.map((entry) => [entry.id, entry]));

export function getListoneEntryById(id) {
  return LISTONE_BY_ID.get(id) || null;
}

function getTeamCandidates(name, team) {
  const n = normalizeForMatch(name);
  const t = normalizeForMatch(team);
  if (!n) return [];

  const pool = LISTONE_BY_TEAM.get(t) || [];
  const exact = pool.filter((c) => c.normName === n);
  if (exact.length) return exact;

  // Fantacalcio.it disambigua gli omonimi con un'iniziale in coda al cognome
  // (es. "Esposito F."): un prefisso nella stessa squadra resta un buon candidato.
  return pool.filter((c) => c.normName.startsWith(n) || n.startsWith(c.normName));
}

// Corrispondenza automatica: solo se univoca nella squadra. Ambiguità o assenza
// di risultati richiedono conferma manuale (vedi collectUnresolvedPlayers).
export function getAutoMatchId(name, team) {
  const candidates = getTeamCandidates(name, team);
  return candidates.length === 1 ? candidates[0].id : null;
}

export function getSuggestedCandidates(name, team) {
  const teamCandidates = getTeamCandidates(name, team);
  if (teamCandidates.length) return teamCandidates;

  // Fallback: stesso nome in qualunque squadra (squadra scritta diversamente,
  // giocatore trasferito dopo la pubblicazione del listone, ecc.).
  const n = normalizeForMatch(name);
  if (!n) return [];
  return LISTONE_INDEXED
    .filter((c) => c.normName === n || c.normName.startsWith(n) || n.startsWith(c.normName))
    .slice(0, 8);
}

export function searchListone(query, limit = 20) {
  const q = normalizeForMatch(query);
  if (!q) return LISTONE_INDEXED.slice(0, limit);
  return LISTONE_INDEXED
    .filter((c) => c.normName.includes(q) || c.normTeam.includes(q))
    .slice(0, limit);
}

// ─── Scelte manuali dell'utente, persistite tra un export e l'altro ─────────
export function loadOverrides() {
  try {
    const raw = localStorage.getItem(OVERRIDES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveOverrides(overrides) {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore
  }
}

export function clearOverrides() {
  try {
    localStorage.removeItem(OVERRIDES_KEY);
  } catch {
    // ignore
  }
}

export function countOverrides() {
  return Object.keys(loadOverrides()).length;
}

function resolvePlayerId(name, team, overrides) {
  const key = buildPlayerKey(name, team);
  if (Object.prototype.hasOwnProperty.call(overrides, key)) {
    return overrides[key]; // numero = id scelto, null = "nessuna corrispondenza" confermato
  }
  return getAutoMatchId(name, team);
}

// Giocatori in rosa senza corrispondenza automatica univoca: vengono sempre mostrati
// all'utente prima dell'export (anche se già risolti in passato), precompilati con
// l'ultima scelta salvata così di norma basta solo confermare.
export function collectAmbiguousPlayers({ teamNames, rosterByTeam }) {
  const overrides = loadOverrides();
  const seen = new Map();

  [...(teamNames || [])].forEach((managerTeam) => {
    const roster = (rosterByTeam?.[managerTeam] || []).filter((p) => !p?.deleted);

    roster.forEach((p) => {
      const snap = p?.playerSnapshot || {};
      const name = snap.name || p.playerName || '';
      const team = snap.team || p.playerTeam || '';
      if (!name) return;
      if (getAutoMatchId(name, team) !== null) return;

      const key = buildPlayerKey(name, team);
      const hasOverride = Object.prototype.hasOwnProperty.call(overrides, key);

      if (!seen.has(key)) {
        seen.set(key, {
          key,
          name,
          team,
          managerTeams: [managerTeam],
          candidates: getSuggestedCandidates(name, team),
          resolvedId: hasOverride ? overrides[key] : undefined,
        });
      } else {
        seen.get(key).managerTeams.push(managerTeam);
      }
    });
  });

  return Array.from(seen.values());
}

export function buildLegheFantacalcioCsv({ teamNames, rosterByTeam }) {
  const overrides = loadOverrides();
  const lines = [];
  const unmatched = [];

  [...(teamNames || [])].forEach((managerTeam) => {
    const safeManagerTeam = String(managerTeam || '').replace(/,/g, ' ');
    const roster = (rosterByTeam?.[managerTeam] || []).filter((p) => !p?.deleted);

    lines.push('$,$,$');

    roster.forEach((p) => {
      const snap = p?.playerSnapshot || {};
      const playerName = snap.name || p.playerName || '';
      const playerTeam = snap.team || p.playerTeam || '';
      const id = resolvePlayerId(playerName, playerTeam, overrides);

      if (!id) {
        unmatched.push({ managerTeam, playerName, playerTeam });
        return;
      }

      lines.push(`${safeManagerTeam},${id},${Number(p.credits || 0)}`);
    });
  });

  return { csv: lines.join('\r\n') + '\r\n', unmatched };
}

export function downloadTextFile(filename, content, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob(['﻿', content], { type: mime });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}
